import { createStyles } from 'antd-style';

import {
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../../theme';
import { FA_SELECTOR } from '../../styles/variables';
import {
  BTN_DEFAULT_SELECTOR,
  borderRadius,
  defBtnDisabled,
  flexCenter,
  listCard,
  listCardActive,
  listCardHover,
} from '../../styles/mixins';
import {
  responsiveMdVsLg,
  responsiveSmVsMd,
} from '../../styles/responsiveMixins';

const LETTER_SHORTCUTS_WIDTH = 25;
const LETTER_SHORTCUTS_CLOSED_WIDTH = 15;

export const useAddressBookStyles = createStyles(
  ({ token, css, cx }, { view, trackVertical } = {}) => {
    const letterShortcuts = cx(
      css({
        position: 'fixed',
        top: '50%',
        transform: 'translate(0, -50%)',
        zIndex: 4,
        ...borderRadius({ size: token.borderRadius, side: 'left' }),
        backgroundColor: ThemeColors.COLOR_WHITE6,
        width: LETTER_SHORTCUTS_WIDTH,
        padding: `${ThemeSpaces.SPACE_NORM}px 0`,
        ...flexCenter(),
        flexDirection: 'column',

        right: LETTER_SHORTCUTS_CLOSED_WIDTH - LETTER_SHORTCUTS_WIDTH,
        transition: `right ${token.motionDurationMid} ease-in-out`,
        '&.open': {
          right: 0,
          boxShadow: `0 3px 10px ${hexToRgbaWithAlpha(
            ThemeColors.COLOR_GREY_BLUISH3,
            0.5
          )}`,
        },
        '&.closed': {
          right: -LETTER_SHORTCUTS_WIDTH,
        },

        '.IconButton': {
          fontSize: 18,
          color: token.colorPrimary,
          borderBottom: `1px solid ${token.colorPrimary}`,
          padding: 1,
          marginBottom: ThemeSpaces.SPACE_XS,
        },

        '.Clickable': {
          fontWeight: 'bold',
          color: token.colorPrimary,
          textTransform: 'uppercase',

          [`@media (min-width: ${token.screenLGMin}px)`]: {
            fontSize: token.fontSizeLG,
            lineHeight: '24px',
          },
          [`@media (max-width: ${token.screenMDMax}px)`]: {
            fontSize: token.fontSize,
            lineHeight: '19px',
          },

          '&.disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
          },
        },
      })
    );

    const sectionHeader = css({
      height: 25,
      lineHeight: '25px',
      backgroundColor: ThemeColors.COLOR_ORANGE3,
      paddingLeft: ThemeSpaces.SPACE_NORM,
      fontSize: token.fontSizeHeading4,
      fontWeight: 'bold',
      color: token.colorWhite,
      textTransform: 'uppercase',

      [`@media (min-width: ${token.screenLGMin}px)`]: {
        borderRadius: token.borderRadiusSM,
      },
    });

    return {
      letterShortcuts,
      container: {
        display: 'flex',
        marginTop: -ThemeSpaces.SPACE_NORM,

        ...responsiveMdVsLg({ paddingRight: 0 }, { paddingRight: 14 }),

        [`> .Scrollbar > .${view} > * > *`]: {
          width: 'auto !important',
          right: -14,
        },

        // Sibling of the (closed) letter shortcuts — reserve room for the rail.
        [`.${letterShortcuts} + & > .Scrollbar > .${trackVertical}`]:
          responsiveMdVsLg(
            {
              marginRight:
                LETTER_SHORTCUTS_CLOSED_WIDTH + ThemeSpaces.SPACE_XXS - 5,
            },
            { marginRight: 0 }
          ),
        // Sibling of the open letter shortcuts — reserve room for the wider rail.
        [`.${letterShortcuts}.open + & > .Scrollbar > .${trackVertical}`]:
          responsiveMdVsLg(
            { marginRight: LETTER_SHORTCUTS_WIDTH + ThemeSpaces.SPACE_XXS - 5 },
            { marginRight: 0 }
          ),
      },
      fixedSection: {
        position: 'absolute',
        left: 0,
        pointerEvents: 'none',

        '&::before, &::after': {
          content: "''",
          position: 'absolute',
          left: 0,
          right: ThemeSpaces.SPACE_NORM_30,
          height: ThemeSpaces.SPACE_NORM,
          backgroundColor: ThemeColors.COLOR_WHITE3,
        },
        '&::before': {
          top: 0,
        },
        '&::after': {
          top: ThemeSpaces.SPACE_NORM + 1,
        },

        ...responsiveMdVsLg(
          { top: 0, right: 0 },
          { top: ThemeSpaces.SPACE_NORM, right: ThemeSpaces.SPACE_NORM }
        ),
      },
      section: {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        marginTop: ThemeSpaces.SPACE_NORM,

        ...responsiveMdVsLg(
          {
            borderTopColor: token.colorWhite,
            marginRight: 0,
            padding: `${ThemeSpaces.SPACE_NORM}px 0 ${ThemeSpaces.SPACE_NORM_20}px 0`,
          },
          {
            borderTopColor: ThemeColors.COLOR_GREY9_4,
            marginRight: 0,
            padding: `${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_30}px ${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM}px`,
          }
        ),
      },
      sectionHeader,
      sectionHeaderClickable: {
        pointerEvents: 'initial',
      },
      entry: {
        ...listCard(token, { spacing: false }),
        position: 'relative',
        padding: `${ThemeSpaces.SPACE_SM}px ${ThemeSpaces.SPACE_NORM}px`,

        ...responsiveMdVsLg(
          {
            margin: `${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_30}px ${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM}px`,
            height: 120,
            '.cols-1-2': { width: '60%' },
            '.col-1': { width: '100%', marginBottom: token.marginXS },
            '.col-2': { width: '100%' },
            '.col-3': { width: '40%' },
          },
          {
            margin: `${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_XL}px ${ThemeSpaces.SPACE_NORM}px ${ThemeSpaces.SPACE_NORM_20}px`,
            height: 80,
            '.cols-1-2': { width: '72.5%' },
            '.col-1': { flex: 1, minWidth: 0 },
            '.col-2': { flex: 1, minWidth: 0 },
            '.col-3': { width: '27.5%' },
          }
        ),

        '&:hover': listCardHover(),
        '&:active': listCardActive(),

        '> .ant-row': {
          width: '100%',
        },
      },
      entryLabel: {
        ...responsiveMdVsLg(
          {
            marginBottom: token.marginXS,
            '&:last-child': { marginBottom: 0 },
          },
          {
            marginBottom: ThemeSpaces.SPACE_NORM0_5,
            '&:last-child': { marginBottom: 0 },
          }
        ),

        '.label': {
          marginBottom: ThemeSpaces.SPACE_XXS,
        },
        '&& .text': {
          fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
          '&.nl-as-newline': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'pre',
          },
        },
      },
      controlButtons: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',

        [`@media (max-width: ${token.screenMDMax}px)`]: {
          position: 'absolute',
          top: ThemeSpaces.SPACE_SM,
          right: ThemeSpaces.SPACE_SM,
        },

        [FA_SELECTOR]: {
          color: token.colorPrimary,
          marginBottom: ThemeSpaces.SPACE_NORM_15,
          '&:last-child': { marginBottom: 0 },
        },
      },
      formDivider: {
        display: 'none',
      },
      form: {
        '.TemplateDrawerForm-Title': {
          [`@media (min-width: ${token.screenLGMin}px)`]: {
            marginBottom: ThemeSpaces.SPACE_NORM0_5,
          },
          [`@media (max-width: ${token.screenMDMax}px)`]: {
            marginBottom: token.marginSM,
          },
        },

        '.TemplateDrawerForm-Controls': {
          marginBottom: token.marginSM,
        },
      },
      controlLinks: {
        height: 0,
        display: 'flex',
        alignItems: 'flex-end',

        [`@media (max-width: ${token.screenSMMax}px)`]: {
          button: {
            paddingLeft: '0 !important',
            paddingRight: '0 !important',

            '.fa-address-book': {
              display: 'none',
            },
          },

          '.ant-col:last-child': {
            borderLeft: `1px solid ${token.colorPrimary}`,
          },
        },

        ...responsiveMdVsLg({ paddingTop: 15 }, { paddingTop: 10 }),

        [`.ant-btn${BTN_DEFAULT_SELECTOR}`]: {
          color: token.colorPrimary,
          gap: ThemeSpaces.SPACE_XS,

          ...defBtnDisabled({
            border: `1px solid ${ThemeColors.COLOR_GREY8_1}`,
            color: token.colorPrimary,
            opacity: 0.25,
          }),

          ...responsiveSmVsMd({ border: 'none', background: 'none' }, {}),
        },
        '.ant-btn': {
          [`> ${FA_SELECTOR}`]: {
            '&:last-child': {
              margin: 0,
            },
          },
        },
      },
      headerRightContent: {
        marginRight: ThemeSpaces.SPACE_NORM_30,

        '&.disabled': {
          opacity: 0.35,
          pointerEvents: 'none',
        },
      },
      noDataDialog: {
        '&&&': {
          '.ant-modal-body': {
            paddingLeft: ThemeSpaces.SPACE_NORM_20,
            paddingRight: ThemeSpaces.SPACE_NORM_20,
          },

          'h1.ant-typography': {
            lineHeight: '31px',
          },

          'span.ant-typography': {
            marginBottom: ThemeSpaces.SPACE_NORM,
          },

          '.ant-btn': {
            height: 30,
          },

          '.ant-btn.ant-btn-background-ghost': {
            width: 75,
          },

          '.ant-btn.btn-blue': {
            width: '100%',
            borderRadius: token.borderRadius,
            '&, &:active, &:hover': {
              backgroundColor: ThemeColors.COLOR_GREY_BLUISH3,
              borderColor: ThemeColors.COLOR_GREY_BLUISH3,
              color: token.colorWhite,
            },
          },
        },
      },
    };
  }
);
